import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { SelectionButton } from '~/bootcamp/Activities/Fractions/components/SelectionButton'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms } from './helpers'

function FractionCompareDenomEqual({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('bootcamp')
  const [answer, setAnswer] = useState<number | null>(null)
  const [rightSideSelected, setRightSideSelected] = useState<boolean | null>(null)
  const [isSmallest, setIsSmallest] = useState<boolean>(true)

  const [numerator, setNumerator] = useState<number>(1)

  const [denominator1, setDenominator1] = useState<number>(1)
  const [denominator2, setDenominator2] = useState<number>(3)

  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const { resetHistory } = getRangesByDifficulty(difficultyLevel)

  useEffect(() => {
    setAnswer(null)
    setIsSmallest(Math.random() < 0.5)
    setRightSideSelected(null)
    const termValue = generateTerms(difficultyLevel, taskHistory)
    setNumerator(termValue.numerator)
    setDenominator1(termValue.denominator1)
    setDenominator2(termValue.denominator2)
  }, [difficultyLevel, taskHistory])

  // denominator1 is 1 only on the placeholder first render.
  useEmitInstance(
    onInstanceChange,
    denominator1 > 1
      ? `\\frac{${numerator}}{${denominator1}} and \\frac{${numerator}}{${denominator2}}; ` +
          `pick the ${isSmallest ? 'smallest' : 'largest'}`
      : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    let selectedAns = null
    if (isSmallest) {
      selectedAns = Math.min(numerator / denominator1, numerator / denominator2)
      onAnswerStateChange(selectedAns === answer)
    } else {
      selectedAns = Math.max(numerator / denominator1, numerator / denominator2)
      onAnswerStateChange(selectedAns === answer)
    }
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [numerator, ...prevState].slice(0, resetHistory)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const handleOnOptionClick = (boolValue: boolean): void => {
    setRightSideSelected(boolValue)
    if (boolValue) {
      setAnswer(numerator / denominator2)
    } else {
      setAnswer(numerator / denominator1)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex w-full justify-center  items-center  text-sm font-normal text-black pb-12 ">
          {t('fractions.comparing_text', {
            largestSmallest: t(isSmallest ? 'fractions.smallest' : 'fractions.largest'),
          })}
        </div>
        <div className="flex space-x-8">
          <SelectionButton
            isSelected={rightSideSelected === false}
            isWrong={rightSideSelected === false && answerState === false}
            onClick={() => {
              handleOnOptionClick(false)
            }}
            size="medium"
          >
            <div className="w-6  text-xl flex flex-col items-center mx-2">
              {numerator}
              <Divider className={`h-0.5 ${rightSideSelected === false ? 'bg-white' : ''}`} />
              {denominator1}
            </div>
          </SelectionButton>
          <SelectionButton
            isSelected={rightSideSelected === true}
            isWrong={rightSideSelected === true && answerState === false}
            onClick={() => {
              handleOnOptionClick(true)
            }}
            size="medium"
          >
            <div className="w-6  text-xl flex flex-col items-center mx-2">
              {numerator}
              <Divider className={`h-0.5 ${rightSideSelected === true ? 'bg-white' : ''}`} />
              {denominator2}
            </div>
          </SelectionButton>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== null,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer(null)
            setRightSideSelected(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCompareDenomEqual
